import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import { Illustration } from 'frontend-utils';
import UpsellBannerIllustration from 'lumen/assets/images/benchmark-insight/upsell/beni-upsell-banner-trial.svg?raw';

interface HotelBenchmarkInsightUpsellBannerUpsellTrialSignature {
  Args: {};
}

export default class HotelBenchmarkInsightUpsellBannerUpsellTrial extends Component<HotelBenchmarkInsightUpsellBannerUpsellTrialSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/trial')
  declare benchmarkInsightTrial: Services['benchmark-insight/trial'];
  @service declare usageAnalytics: Services['usage-analytics'];

  benefits = [
    tKey('benchmark_insight.upsell.trial.banner.benefit.1'),
    tKey('benchmark_insight.upsell.trial.banner.benefit.2'),
    tKey('benchmark_insight.upsell.trial.banner.benefit.3'),
    tKey('benchmark_insight.upsell.trial.banner.benefit.4'),
    tKey('benchmark_insight.upsell.trial.banner.benefit.5'),
  ];

  get hotelHasRequestedTrial(): boolean {
    const { beniUpsellRecord } = this.benchmarkInsightTrial;
    if (!beniUpsellRecord) {
      return false;
    }

    return beniUpsellRecord.trialRequested;
  }

  get isAdminView(): boolean {
    return this.access.hasBenchmarkInsightTrialUpsellEdit;
  }

  @action
  async onClickGetInTouch(): Promise<void> {
    const { beniUpsellRecord, saveBeniUpsellRecordTask } =
      this.benchmarkInsightTrial;

    if (!beniUpsellRecord) {
      return;
    }

    beniUpsellRecord.trialRequested = true;
    await saveBeniUpsellRecordTask.perform();

    this.usageAnalytics.track('Upsell Got In Touch', {
      eventTrigger: 'fake-door',
      product: 'benchmark-insight upsell',
    });
  }

  <template>
    <div class="upsell-banner flex flex-row justify-between">

      <div class="upsell-banner__info flex flex-row justify-center gap-1000">
        <h1 class="upsell-banner__info__title">
          {{#if this.hotelHasRequestedTrial}}
            {{t "benchmark_insight.upsell.banner.title.requested_trial"}}
          {{else}}
            {{t "benchmark_insight.upsell.trial.banner.title"}}
          {{/if}}
        </h1>

        <div class="upsell-banner__info__content">
          {{#if this.hotelHasRequestedTrial}}
            <p>{{t
                "benchmark_insight.upsell.banner.trial.content.trial_requested"
              }}</p>

            <PrismButton
              @buttonType="brand"
              @label={{t "benchmark_insight.upsell.banner.button.learn_more"}}
              @onClick={{this.benchmarkInsightTrial.openHelpArticle}}
              class="mt-400 w-full"
              data-test-id="upsell-trial-learn-more-button"
            />
          {{else}}
            <div class="flex flex-col gap-400">
              {{#each this.benefits as |benefit|}}
                <p class="upsell-banner__info__content__benefit flex flex-row">
                  <PrismIcon @name="checkmark" @size="small" />
                  <span class="ml-200">
                    {{t benefit}}
                  </span>
                </p>
              {{/each}}
            </div>

            <div class="mt-400">
              {{#if this.isAdminView}}
                <PrismButton
                  @buttonType="brand"
                  @iconAfter="arrow-right"
                  @isDisabled={{this.benchmarkInsightTrial.saveBeniUpsellRecordTask.isRunning}}
                  @isLoading={{this.benchmarkInsightTrial.saveBeniUpsellRecordTask.isRunning}}
                  @label={{t "benchmark_insight.light.get_in_touch"}}
                  @onClick={{this.onClickGetInTouch}}
                  class="w-full"
                  data-test-id="upsell-request-trial-button"
                />

                <PrismButton
                  @buttonType="secondary"
                  @label={{t
                    "benchmark_insight.upsell.banner.button.learn_more"
                  }}
                  @onClick={{this.benchmarkInsightTrial.openHelpArticle}}
                  class="mt-200 w-full"
                  data-test-id="upsell-trial-learn-more-button"
                />
              {{else}}
                <p>{{t
                    "benchmark_insight.upsell.banner.trial.content.ask_your_admin"
                  }}</p>

                <PrismButton
                  @buttonType="brand"
                  @label={{t
                    "benchmark_insight.upsell.banner.button.learn_more"
                  }}
                  @onClick={{this.benchmarkInsightTrial.openHelpArticle}}
                  class="mt-400 w-full"
                  data-test-id="upsell-trial-learn-more-button"
                />
              {{/if}}
            </div>
          {{/if}}
        </div>
      </div>

      <div class="upsell-banner__image">
        <Illustration @html={{UpsellBannerIllustration}} />
      </div>
    </div>
  </template>
}
